(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const dialog = id => document.getElementById(id);
  const show = element => { if (element && !element.open) element.showModal(); };
  const status = message => document.querySelectorAll('[data-cart-status]').forEach(el => { el.textContent = message; });
  let cartBusy = false;
  let quickRequest;
  let searchRequest;
  let filterRequest;
  const cardRequests = new WeakMap();
  async function request(url, options = {}) {
    const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers } });
    const body = await response.json();
    if (!response.ok) throw new Error(body.description || body.message || 'Something went wrong. Please try again.');
    return body;
  }
  async function refreshCart() {
    const response = await fetch(`${root()}?sections=cart-content`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your bag changed, but could not refresh. Open the bag page to review it.');
    const sections = await response.json();
    if (!sections['cart-content']) throw new Error('Open the bag page to review your updated bag.');
    document.querySelectorAll('[data-cart-content]').forEach(el => { el.innerHTML = sections['cart-content']; });
    const cart = await request(`${root()}cart.js`);
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = String(cart.item_count); });
  }
  async function changeCart(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    document.querySelectorAll('[data-cart-content]').forEach(el => { el.inert = true; });
    status('Updating bag…');
    try {
      await request(`${root()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      await refreshCart();
      status('Bag updated.');
    } catch (error) {
      status(error.message);
    } finally {
      cartBusy = false;
      document.querySelectorAll('[data-cart-content]').forEach(el => { el.inert = false; });
    }
  }
  function setImage(detail, src, alt) {
    const image = detail.querySelector('[data-main-media] img');
    if (!image) return;
    image.removeAttribute('srcset');
    image.src = src;
    image.alt = alt || '';
    detail.querySelectorAll('[data-image]').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.image === src)); });
  }
  document.addEventListener('click', async event => {
    const close = event.target.closest('[data-close]');
    if (close) { close.closest('dialog').close(); return; }
    if (event.target.closest('[data-search-open]')) {
      show(dialog('search-dialog'));
      dialog('search-dialog').querySelector('input[type="search"]').focus();
      return;
    }
    const bag = event.target.closest('[data-cart-open]');
    if (bag && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      show(dialog('cart-drawer'));
      try { await refreshCart(); } catch (error) { status(error.message); }
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); await changeCart(remove.dataset.cartRemove, 0); return; }
    const image = event.target.closest('[data-image]');
    if (image) { setImage(image.closest('[data-product-detail]'), image.dataset.image, image.dataset.alt); return; }
    const filter = event.target.closest('[data-collection-filter]');
    if (filter && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      filterRequest?.abort();
      filterRequest = new AbortController();
      const section = filter.closest('section');
      const feedback = section.querySelector('[data-collection-status]');
      feedback.textContent = 'Loading pieces…';
      const url = new URL(filter.href);
      url.searchParams.set('section_id', 'main-collection');
      try {
        const response = await fetch(url, { signal: filterRequest.signal });
        if (!response.ok) throw new Error('Could not load pieces.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = doc.querySelector('.product-grid');
        if (!grid) throw new Error('Could not load pieces.');
        section.querySelector('[data-featured-grid]').replaceChildren(...grid.childNodes);
        section.querySelector('[data-collection-count]').textContent = doc.querySelector('[data-result-count]')?.textContent || '';
        section.querySelectorAll('[data-collection-filter]').forEach(link => {
          if (link === filter) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
        });
        feedback.textContent = section.querySelector('[data-collection-count]').textContent;
      } catch (error) { if (error.name !== 'AbortError') { location.href = filter.href; } }
      return;
    }
    const color = event.target.closest('[data-card-color]');
    if (color && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      const card = color.closest('.product-item');
      cardRequests.get(card)?.abort();
      const controller = new AbortController();
      cardRequests.set(card, controller);
      const url = new URL(color.href);
      url.searchParams.set('section_id', 'quick-product');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Could not load color.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const image = doc.querySelector('[data-main-media] img');
        if (!image) throw new Error('Could not load color.');
        card.querySelector('.product-image').querySelector('img')?.replaceWith(image);
        card.querySelector('.product-card').href = color.href;
        card.querySelectorAll('[data-card-color]').forEach(link => {
          if (link === color) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
        });
      } catch (error) { if (error.name !== 'AbortError') location.href = color.href; }
      return;
    }
    const variantLink = event.target.closest('[data-variant-link]');
    if (variantLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      const detail = variantLink.closest('[data-product-detail]');
      const url = new URL(variantLink.href);
      const section = detail.closest('dialog') ? 'quick-product' : 'main-product';
      url.searchParams.set('section_id', section);
      const feedback = detail.querySelector('[data-product-status]');
      const previousQuantity = detail.querySelector('[name="quantity"]').value;
      detail.inert = true;
      feedback.textContent = 'Updating options…';
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Could not update options.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('[data-product-detail]');
        if (!next) throw new Error('Could not update options.');
        next.querySelector('[name="quantity"]').value = previousQuantity;
        detail.replaceWith(next);
        const active = [...next.querySelectorAll('[data-variant-link]')].find(a => a.getAttribute('aria-label') === variantLink.getAttribute('aria-label'));
        active?.focus();
        if (!next.closest('dialog')) { url.searchParams.delete('section_id'); history.replaceState(null, '', url); }
      } catch (error) {
        detail.inert = false;
        feedback.textContent = error.message + ' Open the option link in a new tab to try again.';
      }
      return;
    }
    const quick = event.target.closest('[data-quick-view]');
    if (!quick || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    quickRequest?.abort();
    quickRequest = new AbortController();
    const url = new URL(quick.href);
    url.searchParams.set('section_id', 'quick-product');
    const content = dialog('product-dialog').querySelector('[data-quick-content]');
    content.textContent = 'Loading piece…';
    show(dialog('product-dialog'));
    try {
      const response = await fetch(url, { signal: quickRequest.signal });
      if (!response.ok) throw new Error('Could not load this piece.');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const detail = doc.querySelector('[data-product-detail]');
      if (!detail) throw new Error('Could not load this piece.');
      content.replaceChildren(detail);
    } catch (error) {
      if (error.name === 'AbortError') return;
      content.textContent = error.message + ' ';
      const link = document.createElement('a');
      link.href = quick.href;
      link.textContent = 'View product details ↗';
      content.append(link);
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-cart-key]')) {
      const quantity = Number(event.target.value);
      if (Number.isInteger(quantity) && quantity >= 0) changeCart(event.target.dataset.cartKey, quantity);
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('.purchase-form')) return;
    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    if (button.disabled) return;
    const feedback = form.querySelector('[data-product-status]');
    button.disabled = true;
    feedback.textContent = 'Adding to bag…';
    try {
      await request(`${root()}cart/add.js`, { method: 'POST', body: new FormData(form) });
      feedback.textContent = 'Added to your bag.';
      if (dialog('product-dialog').open) dialog('product-dialog').close();
      show(dialog('cart-drawer'));
      await refreshCart();
      status('Added to your bag.');
    } catch (error) {
      feedback.textContent = error.message;
      status(error.message);
    } finally {
      button.disabled = false;
    }
  });
  let debounce;
  document.getElementById('search')?.addEventListener('input', event => {
    clearTimeout(debounce);
    searchRequest?.abort();
    const query = event.target.value.trim();
    const results = document.getElementById('search-results');
    results.replaceChildren();
    if (!query) return;
    debounce = setTimeout(async () => {
      searchRequest = new AbortController();
      const url = new URL(`${root()}search/suggest`, location.origin);
      url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', section_id: 'predictive-search' });
      try {
        const response = await fetch(url, { signal: searchRequest.signal });
        if (!response.ok) throw new Error('Use Search to view all results.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        results.replaceChildren(...doc.body.childNodes);
      } catch (error) { if (error.name !== 'AbortError') results.textContent = 'Use Search to view all results.'; }
    }, 250);
  });
  document.querySelectorAll('dialog').forEach(element => {
    element.addEventListener('click', event => {
      if (event.target !== element) return;
      const rect = element.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) element.close();
    });
  });
})();
